import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { useFetchWithAuth } from '../session/useFetchWithAuth'
import { EventInvitation, EventInvitationStatus } from './types'

const API_URL = import.meta.env.VITE_EPC_API_URL ?? ''

interface InviteeProfile {
  profile_id: string
  first_name?: string
  last_name?: string
  avatar_url?: string
  color?: string
  role?: string
  company_name?: string
  school_name?: string
}

interface InviterProfile {
  profile_id: string
  first_name?: string
  last_name?: string
  avatar_url?: string
  color?: string
}

interface EventInvitationData {
  event_id: string
  profile_id: string
  status: string
  invited_at: string
  invited_by: string
  responded_at?: string | null
  invitee: InviteeProfile
  inviter: InviterProfile
}

interface PaginationInfo {
  total_count: number
  current_page: number
  total_pages: number
  limit: number
  skip: number
  has_next: boolean
  has_previous: boolean
}

interface StatusCounts {
  PENDING?: number
  ACCEPTED?: number
  DECLINED?: number
  CANCELLED?: number
  FINISHED?: number
  NEEDS_REVIEW?: number
  [key: string]: number | undefined
}

interface FilterInfo {
  status?: string
}

interface EventInvitationsResponse {
  invitations: EventInvitationData[]
  status_counts: StatusCounts
  filters: FilterInfo
  pagination: PaginationInfo
}

interface UseEventInvitationsOptions {
  limit?: number
  skip?: number
  status?: string
}

/**
 * Hook to fetch invitations for a specific event with pagination
 * Returns the list of profiles invited to the event with their invitation status and profile data
 */
export const useEventInvitations = (
  eventId: string,
  options: UseEventInvitationsOptions = {}
) => {
  const fetchWithAuth = useFetchWithAuth()
  const { limit = 20, skip = 0, status } = options

  return useQuery({
    queryKey: ['eventInvitations', eventId, limit, skip, status],
    queryFn: async (): Promise<EventInvitationsResponse> => {
      if (!eventId) {
        throw new Error('Event ID is required')
      }

      const params = new URLSearchParams()
      params.append('limit', limit.toString())
      if (skip > 0) {
        params.append('skip', skip.toString())
      }
      if (status) {
        params.append('status', status)
      }

      const response = await fetchWithAuth(
        `${API_URL}/events/${eventId}/invitations?${params.toString()}`,
        {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
        }
      )

      return response
    },
    enabled: !!eventId,
    staleTime: 60 * 1000, // 1 minute
  })
}

/**
 * Grouped invitations interface for EventDetailView
 */
export interface GroupedInvitations {
  acceptedInvitations: EventInvitation[]
  pendingInvitations: EventInvitation[]
  cancelledInvitations: EventInvitation[]
  reviewableInvitations: EventInvitation[]
  finishedInvitations: EventInvitation[]
  statusCounts: StatusCounts
}

/**
 * Transform API invitation to frontend EventInvitation format
 */
const transformInvitation = (inv: EventInvitationData): EventInvitation => ({
  id: inv.profile_id,
  eventId: inv.event_id,
  profileId: inv.profile_id,
  profileName: inv.invitee
    ? `${inv.invitee.first_name || ''} ${inv.invitee.last_name || ''}`.trim()
    : '',
  profileColor: inv.invitee?.color || '',
  profileIconUrl: inv.invitee?.avatar_url || '',
  status: inv.status as EventInvitationStatus,
  invited_at: inv.invited_at,
  responded_at: inv.responded_at,
})

/**
 * Group invitations by status
 */
const groupInvitations = (
  invitations: EventInvitationData[]
): Omit<GroupedInvitations, 'statusCounts'> => {
  const accepted: EventInvitation[] = []
  const pending: EventInvitation[] = []
  const cancelled: EventInvitation[] = []
  const reviewable: EventInvitation[] = []
  const finished: EventInvitation[] = []

  for (const inv of invitations) {
    const transformed = transformInvitation(inv)
    switch (inv.status) {
      case 'ACCEPTED':
        accepted.push(transformed)
        break
      case 'PENDING':
        pending.push(transformed)
        break
      case 'DECLINED':
      case 'CANCELLED':
        cancelled.push(transformed)
        break
      case 'NEEDS_REVIEW':
        reviewable.push(transformed)
        break
      case 'FINISHED':
        finished.push(transformed)
        break
      default:
        // Unknown status - add to pending as fallback
        pending.push(transformed)
    }
  }

  return {
    acceptedInvitations: accepted,
    pendingInvitations: pending,
    cancelledInvitations: cancelled,
    reviewableInvitations: reviewable,
    finishedInvitations: finished,
  }
}

/**
 * Hook to fetch all invitations for an event, grouped by status
 * Used by the user-facing EventDetailView for displaying attendees
 *
 * @param eventId The ID of the event
 * @returns Grouped invitations matching EventDetailView expectations
 */
export const useGroupedEventInvitations = (
  eventId?: string
): UseQueryResult<GroupedInvitations> => {
  const fetchWithAuth = useFetchWithAuth()

  const getInvitations = async (): Promise<GroupedInvitations> => {
    if (!eventId) {
      throw new Error('Event ID is required')
    }

    // Fetch with high limit to get all invitations
    const response: EventInvitationsResponse = await fetchWithAuth(
      `${API_URL}/events/${eventId}/invitations?limit=250`,
      {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      }
    )

    const grouped = groupInvitations(response.invitations)

    return {
      ...grouped,
      statusCounts: response.status_counts,
    }
  }

  return useQuery({
    queryKey: ['groupedEventInvitations', eventId] as const,
    queryFn: getInvitations,
    retry: false,
    staleTime: 60 * 1000, // Consider data fresh for 1 minute
    enabled: !!eventId,
  })
}
